import { SolutionScenario } from '@/types/scenarios/solution';
import { Anchor, Phone, Calendar, Wrench, DollarSign, Ship, HelpCircle, FileText } from 'lucide-react';

export const marine: SolutionScenario = {
  meta: {
    id: 'marine',
    slug: 'marine',
    name: 'Marine',
    tagline: 'Genies that run the dock office while your team is on the water',
    description: "A genie that books service and winterization, answers slip and storage questions, and captures sea-trial requests when your team is on the water or off the clock.",
    market: 'Marine',
    subMarkets: ['Boat Dealerships', 'Marinas', 'Marine Service Centers', 'Yacht Brokers', 'Marine Equipment'],
    specificNeeds: [
      'Seasonal lead capture optimization',
      'Service and maintenance scheduling',
      'Slip reservation management',
      'Winterization/de-winterization bookings',
      'Sea trial scheduling',
      'Parts and accessory inquiries'
    ],
    commonRoles: ['Sales Representatives', 'Service Advisors', 'Marina Managers', 'Dock Masters'],
    marketSize: '$45 billion US market',
    isFeatured: true
  },

  hero: {
    headline: 'Your dock office, open after hours',
    headlineGradient: 'Your dock office,',
    headlineRest: 'open after hours',
    subheadline: "A genie that books service and winterization, answers slip and storage questions, and captures sea-trial requests when your team is on the water or off the clock.",
  },

  recommendedPlaybooks: [
    'lead-capture',
    'appointment-scheduler',
    'demo-scheduler',
    'proactive-welcome'
  ],

  applications: [
    {
      id: 'lead-capture',
      name: 'Seasonal Lead Capture',
      description: 'Maximize lead capture during peak boating season and off-season inquiries',
      department: 'Sales',
      icon: Phone,
      playbookIds: ['lead-capture', 'proactive-welcome'],
      benefits: [
        'Never miss a lead during busy season',
        'Capture off-season dreamers',
        'Qualify buyers vs. browsers',
        'Immediate CRM integration'
      ]
    },
    {
      id: 'sea-trials',
      name: 'Sea Trial Scheduling',
      description: 'Schedule on-water demonstrations with intelligent availability management',
      department: 'Sales',
      icon: Ship,
      playbookIds: ['demo-scheduler'],
      benefits: [
        'Weather-aware scheduling suggestions',
        'Captain availability coordination',
        'Automated confirmations',
        'Reduce no-shows significantly'
      ]
    },
    {
      id: 'service-booking',
      name: 'Marine Service Booking',
      description: 'Handle service appointments for maintenance, repairs, and seasonal work',
      department: 'Service',
      icon: Wrench,
      playbookIds: ['appointment-scheduler'],
      benefits: [
        'Winterization/spring commissioning bookings',
        'Routine maintenance scheduling',
        'Haul-out coordination',
        'Priority scheduling for urgent repairs'
      ]
    },
    {
      id: 'slip-reservations',
      name: 'Slip & Storage Management',
      description: 'Manage marina slip reservations and storage inquiries',
      department: 'Marina Operations',
      icon: Anchor,
      playbookIds: ['appointment-scheduler'],
      benefits: [
        '24/7 availability inquiries',
        'Transient slip bookings',
        'Seasonal storage reservations',
        'Waitlist management'
      ]
    },
    {
      id: 'parts-accessories',
      name: 'Parts & Accessories Inquiries',
      description: 'Answer questions about marine parts, accessories, and equipment availability',
      department: 'Parts',
      icon: HelpCircle,
      playbookIds: ['interactive-faq'],
      benefits: [
        'Instant parts availability checks',
        'Accessory recommendations',
        'Compatibility verification',
        'Order placement assistance'
      ]
    },
    {
      id: 'financing-insurance',
      name: 'Financing & Insurance Support',
      description: 'Guide customers through boat financing options and insurance inquiries',
      department: 'Finance',
      icon: FileText,
      playbookIds: ['lead-capture', 'interactive-faq'],
      benefits: [
        'Pre-qualification assistance',
        'Payment calculator guidance',
        'Insurance quote requests',
        'Documentation collection'
      ]
    }
  ],

  challenges: [
    {
      title: 'Extreme Seasonality',
      description: 'Peak season creates overwhelming demand while off-season requires nurturing leads',
      impact: 'high',
      affectedAreas: ['Staff capacity', 'Lead management', 'Revenue consistency']
    },
    {
      title: 'Complex Service Scheduling',
      description: 'Marine service involves weather, tides, haul-outs, and specialized technicians',
      impact: 'high',
      affectedAreas: ['Service efficiency', 'Customer satisfaction', 'Revenue optimization']
    },
    {
      title: 'High-Value Customer Expectations',
      description: 'Boat buyers expect premium service and immediate attention',
      impact: 'medium',
      affectedAreas: ['Customer experience', 'Brand reputation', 'Sales conversion']
    },
    {
      title: 'After Hours Inquiries',
      description: 'Customers often research and reach out during evenings and weekends',
      impact: 'high',
      affectedAreas: ['Lead capture', 'Sales opportunities', 'Competitive advantage']
    }
  ],

  solutions: [
    {
      challenge: 'Peak season call overflow',
      solution: 'Deploy Voice Genie to handle routine inquiries and scheduling',
      implementation: 'Integrate with phone system, configure for boat-specific conversations',
      expectedResults: [
        '50% reduction in missed calls during peak',
        'Staff focus on high-value interactions',
        'Consistent customer experience'
      ]
    },
    {
      challenge: 'Off-season lead nurturing',
      solution: 'Voice-powered outreach and engagement for boat show season',
      implementation: 'Proactive campaigns for winterization, spring prep, and boat show follow-ups',
      expectedResults: [
        'Year-round lead engagement',
        '30% higher spring conversion rates',
        'Stronger customer relationships'
      ]
    },
    {
      challenge: 'Service scheduling complexity',
      solution: 'Intelligent booking that considers weather, tide schedules, and technician skills',
      implementation: 'Connect to service calendar with marine-specific scheduling logic',
      expectedResults: [
        '40% reduction in scheduling conflicts',
        'Optimized technician utilization',
        'Happier customers and staff'
      ]
    }
  ],

  integrations: {
    marketSpecific: [
      {
        name: 'DockMaster',
        category: 'Marina Management',
        description: 'Comprehensive marina management software',
        required: false,
        popularityScore: 85
      },
      {
        name: 'Molo',
        category: 'Marina Management',
        description: 'Modern marina management platform',
        required: false,
        popularityScore: 78
      },
      {
        name: 'IBS',
        category: 'Dealer Management',
        description: 'Marine dealership management system',
        required: false,
        popularityScore: 82
      },
      {
        name: 'Lightspeed',
        category: 'Dealer Management',
        description: 'Marine and powersports dealer software',
        required: false,
        popularityScore: 80
      }
    ],
    common: [
      {
        name: 'Salesforce',
        category: 'CRM',
        description: 'Enterprise CRM platform',
        required: false,
        popularityScore: 95
      },
      {
        name: 'HubSpot',
        category: 'CRM',
        description: 'Marketing and sales platform',
        required: false,
        popularityScore: 90
      }
    ]
  },

  metrics: {
    averageImplementationTime: '1-2 weeks',
    typicalROI: '380%',
    adoptionRate: '91% customer satisfaction',
    customerSatisfaction: '4.7/5'
  },

  demoConfig: {
    firstMessage: "Ahoy! Thanks for reaching out. I can help you find the perfect boat, schedule a sea trial, book service or maintenance, or answer questions about our marina. How can I help you today?",
    systemPrompt: "You are a knowledgeable marine industry assistant. Help customers with boat inquiries, schedule sea trials and service appointments, and manage marina-related questions. Be familiar with boating terminology and seasonal considerations. Always aim to convert interest into appointments.",
    marketContext: "Marine dealership and marina handling sales, service, and slip inquiries",
    exampleScenarios: [
      "I'm looking for a center console for fishing",
      "I need to schedule winterization",
      "Do you have any transient slips available?",
      "What's the trade-in value on my current boat?"
    ]
  },

  caseStudies: [
    {
      companyName: 'Coastal Marine Group',
      companySize: '3 locations with marina',
      location: 'East Coast United States',
      challenge: 'Overwhelmed during boat show season, missing 35% of incoming calls',
      implementation: 'Deployed Help Genie for lead capture, service scheduling, and marina inquiries',
      timeline: '10 days',
      results: [
        {
          metric: 'Call Answer Rate',
          value: '98%',
          improvement: 'From 65%'
        },
        {
          metric: 'Lead Capture',
          value: '+52%',
          improvement: 'Year over year'
        },
        {
          metric: 'Service Bookings',
          value: '+35%',
          improvement: 'Winterization season'
        },
        {
          metric: 'Customer Satisfaction',
          value: '4.8/5',
          improvement: 'Up from 4.0'
        }
      ],
      quote: {
        text: "Boat show season used to be chaos. Now Help Genie captures every lead while our team focuses on closing deals. Our service department has never been more efficient.",
        author: 'Captain Dave Wilson',
        title: 'Owner'
      }
    }
  ],

  resources: [
    {
      type: 'guide',
      title: 'Marine Industry Voice Genie Playbook',
      description: 'Implementing voice technology for boat dealers and marinas',
      url: '/resources/marine-voice-guide'
    },
    {
      type: 'webinar',
      title: 'Maximizing Boat Show Season ROI',
      description: 'How to capture and convert more leads during peak season',
      url: '/webinars/boat-show-leads'
    },
    {
      type: 'template',
      title: 'Winterization Outreach Scripts',
      description: 'Pre-built voice scripts for seasonal service campaigns',
      url: '/templates/winterization-scripts'
    }
  ],

  faqs: [
    {
      question: 'Does the genie understand marine terminology?',
      answer: 'Yes, Help Genie is trained on marine industry terminology including boat types, engine specifications, marina operations, and seasonal service needs. It can discuss everything from outboard maintenance to slip dimensions.'
    },
    {
      question: 'How does it handle weather-dependent scheduling?',
      answer: 'The system can be configured to consider weather conditions for sea trials and certain service work, suggesting alternative times when conditions may be unfavorable.'
    },
    {
      question: 'Can it manage seasonal service campaigns?',
      answer: 'Absolutely. Help Genie excels at proactive outreach for winterization, spring commissioning, and routine maintenance reminders, helping you maximize service revenue.'
    }
  ],

  pageContent: {
    scenariosHeader: {
      title: "Peak season is short\nevery lead counts",
      subtitle: "See how your Genie handles real marine inquiries — booking sea trials, capturing boat show leads, and scheduling service while your team is on the water.",
    },
    chatScenarios: [
      {
        id: 'sea_trial',
        title: 'Sea Trial Booking',
        messages: [
          { type: 'user' as const, text: "I saw a 28-foot center console at the boat show and I'd love to take it out on the water.", delay: 300 },
          { type: 'genie' as const, text: "Great choice! That's one of our most popular models. I can schedule a sea trial for you. Do you have a preferred day?", delay: 1200 },
          { type: 'user' as const, text: "This weekend would be ideal if the weather looks good", delay: 2500 },
          { type: 'genie' as const, text: "Saturday morning looks calm and clear — perfect conditions. We have 9 AM and 11 AM open. Which do you prefer?", delay: 3400 },
          { type: 'user' as const, text: "9 AM works. My name is Jeff Collins, 555-876-2341", delay: 4600 },
          { type: 'genie' as const, text: "You're booked for Saturday at 9 AM, Jeff. Our captain will meet you at the dock. Bring sunglasses — it's going to be a beautiful day on the water.", delay: 5500 },
        ],
        outcomes: [
          { id: '1', text: 'Model interest captured', delay: 6500 },
          { id: '2', text: 'Weather-aware scheduling', delay: 6700 },
          { id: '3', text: 'Sea trial booked with captain', delay: 6900 },
          { id: '4', text: 'Contact details in CRM', delay: 7100 },
          { id: '5', text: 'Hot lead flagged for sales follow-up', delay: 7300 },
        ],
      },
      {
        id: 'winterization',
        title: 'Winterization Booking',
        messages: [
          { type: 'user' as const, text: "Fall is coming — I need to get my boat winterized. It's a 32-foot Sea Ray with twin Mercruisers.", delay: 300 },
          { type: 'genie' as const, text: "Smart timing — our winterization slots fill up fast. For a 32-foot Sea Ray with twin sterndrives, we'd recommend our full winterization package. That covers engine fogging, coolant, stabilizer, and shrink wrap.", delay: 1200 },
          { type: 'user' as const, text: "What does that run?", delay: 2500 },
          { type: 'genie' as const, text: "For twin sterndrives with shrink wrap, it's typically in the $1,200–$1,500 range depending on specifics. I can get you an exact quote and book your haul-out. When works for you?", delay: 3400 },
          { type: 'user' as const, text: "Early October ideally. Mike Reynolds, 555-934-5612", delay: 4600 },
          { type: 'genie' as const, text: "Perfect, Mike. I have you down for the first week of October. Our service manager will confirm the exact date and final pricing within 24 hours.", delay: 5500 },
        ],
        outcomes: [
          { id: '1', text: 'Boat specs captured', delay: 6500 },
          { id: '2', text: 'Service package recommended', delay: 6700 },
          { id: '3', text: 'Pricing range provided', delay: 6900 },
          { id: '4', text: 'Haul-out tentatively scheduled', delay: 7100 },
          { id: '5', text: 'Service revenue secured', delay: 7300 },
        ],
      },
      {
        id: 'slip_inquiry',
        title: 'Slip Reservation',
        messages: [
          { type: 'user' as const, text: "We're cruising up from Florida and need a transient slip for next weekend. We're a 42-foot cabin cruiser, 13-foot beam.", delay: 300 },
          { type: 'genie' as const, text: "Welcome! We have a few slips that would fit a 42-footer with that beam. How many nights are you looking to stay?", delay: 1200 },
          { type: 'user' as const, text: "Three nights, Friday through Sunday. Do you have shore power and pump-out?", delay: 2500 },
          { type: 'genie' as const, text: "We do — 30 and 50 amp shore power at every slip, plus pump-out service. For a 42-foot slip, it's $3.50 per foot per night. That would be $441 for three nights.", delay: 3400 },
          { type: 'user' as const, text: "That works. Book it under Captain Tom Miller, 555-612-8890", delay: 4600 },
          { type: 'genie' as const, text: "You're all set, Captain Miller. Slip B-14, Friday through Sunday. I'll send a confirmation with directions and our marina amenities. We look forward to having you!", delay: 5500 },
        ],
        outcomes: [
          { id: '1', text: 'Vessel dimensions verified', delay: 6500 },
          { id: '2', text: 'Amenities confirmed', delay: 6700 },
          { id: '3', text: 'Pricing calculated', delay: 6900 },
          { id: '4', text: 'Slip reserved and assigned', delay: 7100 },
          { id: '5', text: 'Confirmation sent', delay: 7300 },
        ],
      },
      {
        id: 'boat_inquiry',
        title: 'Boat Sales Inquiry',
        messages: [
          { type: 'user' as const, text: "I'm looking for a family-friendly pontoon, something around 22–24 feet with a good motor. What do you have?", delay: 300 },
          { type: 'genie' as const, text: "Great choice for family boating! We currently have several models in that range. Are you thinking new or pre-owned, and do you have a budget in mind?", delay: 1200 },
          { type: 'user' as const, text: "Open to either. Hoping to stay under $60K", delay: 2500 },
          { type: 'genie' as const, text: "We have a few options that fit. Our new Bennington 22SX starts around $48K, and we have a certified pre-owned Harris 24 at $42K with low hours. Want to come see them?", delay: 3400 },
          { type: 'user' as const, text: "Yeah, this Saturday would be great", delay: 4600 },
          { type: 'genie' as const, text: "Saturday works. I'll have both models pulled up for you. What's your name and number so our sales team can confirm?", delay: 5500 },
        ],
        outcomes: [
          { id: '1', text: 'Boat type and size needs captured', delay: 6500 },
          { id: '2', text: 'Budget qualified', delay: 6700 },
          { id: '3', text: 'Inventory matched to needs', delay: 6900 },
          { id: '4', text: 'Showroom visit scheduled', delay: 7100 },
          { id: '5', text: 'Qualified lead for sales team', delay: 7300 },
        ],
      },
      {
        id: 'parts_after_hours',
        title: 'After Hours',
        messages: [
          { type: 'user' as const, text: "Hey, I know you're closed but I cracked my prop coming into the dock today. I have a trip planned for Saturday.", delay: 300 },
          { type: 'genie' as const, text: "Sorry to hear about the prop! I can help get that sorted. What's the make and model of your motor?", delay: 1200 },
          { type: 'user' as const, text: "It's a Yamaha F150, 2021", delay: 2500 },
          { type: 'genie' as const, text: "Got it — Yamaha F150. I'll flag this as urgent with our parts and service team so they can check prop availability first thing tomorrow. If we have it in stock, we can likely get you on the water before Saturday.", delay: 3400 },
          { type: 'user' as const, text: "That'd be a lifesaver. Rob Sanchez, 555-478-1234", delay: 4600 },
          { type: 'genie' as const, text: "Got it, Rob. Our team will call you by 9 AM tomorrow with availability and timing. Tight turnaround but we'll do our best to get you out for Saturday.", delay: 5500 },
        ],
        outcomes: [
          { id: '1', text: 'Engine details captured', delay: 6500 },
          { id: '2', text: 'Urgency flagged (trip deadline)', delay: 6700 },
          { id: '3', text: 'Parts request queued', delay: 6900 },
          { id: '4', text: 'Morning callback scheduled', delay: 7100 },
          { id: '5', text: 'Service revenue opportunity saved', delay: 7300 },
        ],
      },
    ],
    useCasesHeader: {
      title: "Don't Let Leads Drift Away",
      subtitle: "Boating season is short and every inquiry matters. Your Genie captures leads, books sea trials, and fills your service calendar — even when you're on the water.",
    },
    useCaseCards: [
      {
        id: 'seasonal-leads',
        title: 'Seasonal Lead Capture',
        description: "Peak season floods your phone lines. Your Genie captures every boat show follow-up, website inquiry, and walk-in callback — day or night.",
        bullets: [
          'Capture boat show leads automatically',
          'Qualify buyers by budget, boat type, and timeline',
          'Follow up with off-season dreamers',
          'Never lose a lead to a competitor',
        ],
        image: '/solutions-pages/marine/usecase-seasonal-leads.webp',
      },
      {
        id: 'sea-trial-booking',
        title: 'Sea Trial & Showroom Visits',
        description: "Let customers book sea trials and showroom visits on their schedule. Your Genie coordinates captain availability and weather conditions.",
        bullets: [
          'Weather-aware scheduling suggestions',
          'Captain and vessel availability synced',
          'Automatic confirmations and reminders',
          'Reduce no-shows by 35%',
        ],
        image: '/solutions-pages/marine/usecase-sea-trials.webp',
      },
      {
        id: 'service-winterization',
        title: 'Service & Winterization',
        description: "Fill your service calendar with winterization, spring commissioning, and routine maintenance — booked automatically around the clock.",
        bullets: [
          'Winterization packages recommended by vessel',
          'Haul-out scheduling and coordination',
          'Seasonal maintenance reminders',
          'Upsell annual service plans',
        ],
        image: '/solutions-pages/marine/usecase-service-winterization.webp',
      },
      {
        id: 'slip-management',
        title: 'Slip & Storage Reservations',
        description: "Transient boaters and seasonal renters can check availability and book slips instantly — no phone tag, no missed revenue.",
        bullets: [
          'Real-time slip availability',
          'Transient and seasonal bookings',
          'Amenities and pricing info provided',
          'Waitlist management for full marinas',
        ],
        image: '/solutions-pages/marine/usecase-slip-reservations.webp',
      },
    ],
    finalCTA: {
      title: "Your Marina Never Closes",
      subtitle: "While your boats are docked for the night, your Genie is booking sea trials, capturing leads, and filling your service calendar for the season ahead.",
      pillars: [
        {
          title: 'Always\nCapturing',
          gradient: 'from-purple-400 to-blue-400',
          description: 'Every inquiry answered — boat show leads, website calls, and after hours dreamers.',
        },
        {
          title: 'Always\nBooking',
          gradient: 'from-blue-400 to-cyan-400',
          description: 'Sea trials, service appointments, and slip reservations — booked 24/7.',
        },
        {
          title: 'Always\nSelling',
          gradient: 'from-cyan-400 to-teal-400',
          description: 'Qualified leads with budget, specs, and timeline — ready for your sales team.',
        },
      ],
      backgroundImage: '/solutions-pages/marine/cta-background.webp',
    },
  }
};
